import { Dispatch, SetStateAction } from "react";
import ConversationComponent, {
  ConversationComponentProps,
} from "./ConversationComponent";
import { Role } from "@prisma/client";
import { getName, getRoleLabel } from "@ac-expo/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../Dialog";
import IconButton from "../IconButton";
import { cn } from "../../../utils";

interface ConversationDialogProps extends ConversationComponentProps {
  participant: string;
  setParticipant: Dispatch<SetStateAction<string | null>>;
  pictureUrl?: string;
  roleColorClassname?: string;
  fromBackoffice?: boolean;
  cannotSendMessage?: boolean;
}

const roleOrder: Role[] = [
  "acExpo",
  "exhibitor",
  "exhibitorCommercial",
  "organiser",
  "tourismOperator",
  "placeManager",
];

export function ConversationDialog({
  participant,
  setParticipant,
  conversation,
  pictureUrl,
  roleColorClassname,
  fromBackoffice,
  cannotSendMessage,
  ...props
}: ConversationDialogProps) {
  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) setParticipant("");
  };

  const { with: conversationWith } = conversation ?? {};
  if (conversationWith?.id === "AC-EXPO")
    conversationWith.roles = [{ role: "acExpo" }];

  const roles = conversationWith?.roles ?? [];
  const displayRoles =
    roles
      .sort((a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role))
      .slice(0, 2) ?? [];
  return (
    <Dialog open={participant !== ""} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-full max-h-full max-w-full flex-col gap-0 bg-white p-0 sm:rounded-none"
        withCloseButton={false}
      >
        {conversationWith && (
          <DialogHeader
            className={cn(
              "flex h-fit flex-row items-center justify-between gap-2 p-4",
              roleColorClassname,
              {
                "text-white": roleColorClassname,
              }
            )}
          >
            <div className="flex shrink-0 items-center gap-2">
              <DialogClose asChild>
                <IconButton
                  svgType="arrowLeft"
                  svgProps={{
                    color: roleColorClassname ? "white" : "black",
                    size: 24,
                  }}
                  variant={"hidden"}
                />
              </DialogClose>

              <img
                src={pictureUrl ? pictureUrl : "/images/anonymous_avatar.svg"}
                alt={"profile"}
                className="aspect-square size-12 rounded-full"
              />
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-sm font-bold">
                  {conversationWith.id === "AC-EXPO"
                    ? "Connect'Events"
                    : getName(conversationWith)}
                </DialogTitle>
                <p className="max-w-40 truncate text-sm font-semibold">
                  {conversationWith.businessName ?? null}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {displayRoles.map((roleObject) => (
                <p
                  key={roleObject.role}
                  className={cn(
                    "h-fit w-min text-nowrap rounded-full border border-white bg-slate-200 px-2 text-xs",
                    roleColorClassname
                  )}
                >
                  {roleObject.role === "acExpo"
                    ? "SAV"
                    : getRoleLabel(roleObject.role)}
                </p>
              ))}
              {roles.length > displayRoles.length && (
                <p
                  key={"additional"}
                  className={cn(
                    "h-fit w-min text-nowrap rounded-full border border-white px-2 text-xs",
                    roleColorClassname
                  )}
                >
                  +{roles.length - displayRoles.length}
                </p>
              )}
            </div>
          </DialogHeader>
        )}
        <div className="h-full overflow-y-auto">
          <ConversationComponent
            conversation={conversation}
            withHeader={false}
            pictureUrl={pictureUrl}
            fromBackoffice={fromBackoffice}
            cannotSendMessage={cannotSendMessage}
            {...props}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConversationDialog;
